import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Input,
  Row,
  Select,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import api from "../../api/axios";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: "New", value: "new" },
  { label: "Contacted", value: "contacted" },
  { label: "Closed", value: "closed" },
];

type RawLead = Record<string, any>;

type LeadRecord = {
  id: string | number | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  companyName: string;
  source: string;
  status: string;
  createdAt: string;
  createdAtValue: number | null;
  message: string;
};

const pickValue = (lead: RawLead, keys: string[]) => {
  for (const key of keys) {
    if (lead && Object.prototype.hasOwnProperty.call(lead, key)) {
      const value = lead[key];
      if (value !== undefined && value !== null) return value;
    }
  }
  return undefined;
};

const normalizeText = (value: any) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const formatValue = (value: any) => {
  const text = normalizeText(value);
  return text ? text : "—";
};

const formatDate = (value: number | null) =>
  value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "—";

const formatStatus = (status: string) => {
  if (!status) return "—";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const statusColor = (status: string) => {
  switch (status) {
    case "new":
      return "green";
    case "contacted":
      return "blue";
    case "closed":
      return "default";
    default:
      return "default";
  }
};

const toTimestamp = (value: string) => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

const normalizeLead = (lead: RawLead): LeadRecord => {
  const createdAtRaw = normalizeText(
    pickValue(lead, ["createdAt", "created_at", "created", "createdOn", "created_on"])
  );
  const statusRaw = normalizeText(
    pickValue(lead, ["status", "leadStatus", "lead_status"])
  );

  return {
    id: pickValue(lead, ["id", "ID", "leadId", "lead_id"]) ?? null,
    firstName: normalizeText(
      pickValue(lead, ["firstName", "first_name", "firstname", "first"])
    ),
    lastName: normalizeText(
      pickValue(lead, ["lastName", "last_name", "lastname", "last"])
    ),
    email: normalizeText(pickValue(lead, ["email", "Email"])),
    phone: normalizeText(
      pickValue(lead, ["phone", "phoneNumber", "phone_number"])
    ),
    country: normalizeText(pickValue(lead, ["country", "Country"])),
    companyName: normalizeText(
      pickValue(lead, ["companyName", "company_name", "company"])
    ),
    source: normalizeText(pickValue(lead, ["source", "Source"])),
    status: statusRaw.toLowerCase(),
    createdAt: createdAtRaw,
    createdAtValue: toTimestamp(createdAtRaw),
    message: normalizeText(pickValue(lead, ["message", "notes", "note"])),
  };
};

export default function ResellerLeadsPage() {
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [countryFilter, setCountryFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeLead, setActiveLead] = useState<LeadRecord | null>(null);

  const loadLeads = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/ResellerLeads");
      const data = res?.data;
      const list = Array.isArray(data)
        ? data
        : data?.items ?? data?.data ?? data?.leads ?? [];
      const normalized = Array.isArray(list) ? list.map(normalizeLead) : [];
      setLeads(normalized);
    } catch {
      setError("Failed to load reseller leads. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const countryOptions = useMemo(() => {
    const set = new Set<string>();
    leads.forEach((lead) => {
      if (lead.country) set.add(lead.country);
    });
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map((country) => ({ label: country, value: country }));
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const term = searchValue.trim().toLowerCase();
    const start = dateRange?.[0]?.startOf("day").valueOf();
    const end = dateRange?.[1]?.endOf("day").valueOf();

    return leads
      .filter((lead) => {
        if (statusFilter && lead.status !== statusFilter) return false;
        if (countryFilter && lead.country !== countryFilter) return false;

        if (term) {
          const haystack =
            `${lead.firstName} ${lead.lastName} ${lead.email} ${lead.companyName}`.toLowerCase();
          if (!haystack.includes(term)) return false;
        }

        if (start && end) {
          const createdAtValue = lead.createdAtValue;
          if (!createdAtValue || createdAtValue < start || createdAtValue > end) {
            return false;
          }
        }

        return true;
      })
      .slice()
      .sort((a, b) => (b.createdAtValue ?? 0) - (a.createdAtValue ?? 0));
  }, [leads, statusFilter, countryFilter, dateRange, searchValue]);

  const columns: ColumnsType<LeadRecord> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => {
        const numA = Number(a.id);
        const numB = Number(b.id);
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
        return String(a.id ?? "").localeCompare(String(b.id ?? ""));
      },
      render: (value) => formatValue(value),
    },
    {
      title: "First name",
      dataIndex: "firstName",
      key: "firstName",
      sorter: (a, b) => a.firstName.localeCompare(b.firstName),
      render: (value) => formatValue(value),
    },
    {
      title: "Last name",
      dataIndex: "lastName",
      key: "lastName",
      sorter: (a, b) => a.lastName.localeCompare(b.lastName),
      render: (value) => formatValue(value),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
      render: (value) => formatValue(value),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (value) => formatValue(value),
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      sorter: (a, b) => a.country.localeCompare(b.country),
      render: (value) => formatValue(value),
    },
    {
      title: "Company name",
      dataIndex: "companyName",
      key: "companyName",
      sorter: (a, b) => a.companyName.localeCompare(b.companyName),
      render: (value) => formatValue(value),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (value) => formatValue(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (value: string) =>
        value ? <Tag color={statusColor(value)}>{formatStatus(value)}</Tag> : "—",
    },
    {
      title: "Created at",
      dataIndex: "createdAtValue",
      key: "createdAtValue",
      sorter: (a, b) => (a.createdAtValue ?? 0) - (b.createdAtValue ?? 0),
      defaultSortOrder: "descend",
      render: (_value, record) => formatDate(record.createdAtValue),
    },
    {
      title: "Action",
      key: "action",
      render: (_value, record) => (
        <Button
          type="link"
          onClick={() => {
            setActiveLead(record);
            setDrawerOpen(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} md={16}>
          <Title level={3} style={{ margin: 0 }}>
            Reseller Leads
          </Title>
          <Text type="secondary">Website contact requests</Text>
        </Col>
        <Col xs={24} md={8}>
          <Button onClick={loadLeads} loading={loading} block>
            Refresh
          </Button>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Select
              placeholder="Status"
              options={STATUS_OPTIONS}
              allowClear
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Country"
              options={countryOptions}
              allowClear
              showSearch
              optionFilterProp="label"
              value={countryFilter}
              onChange={(value) => setCountryFilter(value)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} md={6}>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange ?? undefined}
              onChange={(dates) => setDateRange(dates ?? null)}
              allowClear
            />
          </Col>
          <Col xs={24} md={6}>
            <Input
              placeholder="Search by name, email, or company"
              allowClear
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      <Card style={{ marginTop: 16 }}>
        <Table
          rowKey={(record, index) =>
            String(record.id ?? record.email ?? record.createdAt ?? index)
          }
          columns={columns}
          dataSource={filteredLeads}
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: "No leads yet" }}
        />
      </Card>

      <Drawer
        title="Reseller lead"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setActiveLead(null);
        }}
        width={520}
      >
        {activeLead ? (
          <div>
            <Text type="secondary">Lead details</Text>
            <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
              <div>
                <Text strong>ID:</Text> {formatValue(activeLead.id)}
              </div>
              <div>
                <Text strong>First name:</Text> {formatValue(activeLead.firstName)}
              </div>
              <div>
                <Text strong>Last name:</Text> {formatValue(activeLead.lastName)}
              </div>
              <div>
                <Text strong>Email:</Text> {formatValue(activeLead.email)}
              </div>
              <div>
                <Text strong>Phone:</Text> {formatValue(activeLead.phone)}
              </div>
              <div>
                <Text strong>Country:</Text> {formatValue(activeLead.country)}
              </div>
              <div>
                <Text strong>Company name:</Text> {formatValue(activeLead.companyName)}
              </div>
              <div>
                <Text strong>Source:</Text> {formatValue(activeLead.source)}
              </div>
              <div>
                <Text strong>Status:</Text>{" "}
                {activeLead.status ? (
                  <Tag color={statusColor(activeLead.status)}>
                    {formatStatus(activeLead.status)}
                  </Tag>
                ) : (
                  "—"
                )}
              </div>
              <div>
                <Text strong>Created at:</Text> {formatDate(activeLead.createdAtValue)}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <Text type="secondary">Message</Text>
              <div
                style={{
                  marginTop: 8,
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  whiteSpace: "pre-wrap",
                }}
              >
                {formatValue(activeLead.message)}
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}
